import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, map, tap, timeout } from 'rxjs/operators';

import { AppState, ConfigService } from '@fcom/core';
import { Profile, ProfileTier } from '@fcom/core-api/login';
import { TzDate } from '@fcom/core/utils';

import { AuthActions } from '../store/actions';
import { BookingRef } from '../interfaces/upgrades.interface';
import { InvalidUpgradeError, UpgradeError } from '../interfaces/upgrade-error.interface';
import { Buyer, UpgradeStatus, UpgradeStatusReason, VoucherType, Passengers } from '../api/models';
import { FINNAIR_LIKE_AIRLINECODES, OFFERS_V2_TIMEOUT, WAITLIST_CUTOFF } from '../constants';
import { getVoucherAmount, getMinPaxAmountToUpgrade, getFlightsAsBookingLocationCodes } from '../utils';
import { GetOffersV2Response } from '../api/models/get-offers-v-2-response';
import { FlightsV2 } from '../api/models/flights-v-2';
import { OffersService as ApiOffersService } from '../api/services';
import { fixtures } from '../fixtures/index';

@Injectable({
  providedIn: 'root',
})
export class OffersService {
  constructor(
    private store$: Store<AppState>,
    private apiOffersService: ApiOffersService,
    private configService: ConfigService
  ) {}

  getOffers(
    booking: BookingRef,
    profile: Profile,
    shouldUseMemberEndpoint: boolean
  ): Observable<Required<GetOffersV2Response>> {
    let getUpgradeRequest$: Observable<GetOffersV2Response>;

    if (this.configService.cfg.enableUpgradeBenefitInUpgrade && profile && fixtures[booking.recLoc]) {
      getUpgradeRequest$ = of(fixtures[booking.recLoc]).pipe(delay(1000));
    } else {
      getUpgradeRequest$ = shouldUseMemberEndpoint
        ? this.apiOffersService.getOffersV2Authenticated(booking)
        : this.apiOffersService.getOffersV2(booking);
    }

    return getUpgradeRequest$.pipe(
      timeout(OFFERS_V2_TIMEOUT),
      // eslint-disable-next-line rxjs/no-implicit-any-catch
      catchError((e) => {
        const errorDetail = e?.error?.details ? e.error.details : '';
        if (
          errorDetail === InvalidUpgradeError.infant ||
          errorDetail === InvalidUpgradeError.paxCount ||
          errorDetail === InvalidUpgradeError.group ||
          errorDetail === InvalidUpgradeError.invalidSSR
        ) {
          return throwError(() => UpgradeError.notPossible);
        }

        if (errorDetail === InvalidUpgradeError.invalidBookingRef) {
          return throwError(() => UpgradeError.httpError);
        }

        return throwError(() => UpgradeError.notAvailable);
      }),
      map((response) => {
        if (
          !response.booking ||
          !response.booking.flights ||
          !response.booking.flights.length ||
          !response.booking.passengers ||
          !response.booking.passengers.length
        ) {
          throw new Error(UpgradeError.httpError);
        }
        return response;
      }),
      tap((response) => this.setSummary(response.booking.flights)),
      map((response) => {
        const flights = response.booking.flights;
        const passengers = response.booking.passengers;

        // Check if any flights in progress and throw error
        this.checkFlightStatusAndThrow(flights, UpgradeStatus.InProgress);

        // Check if any flights marked Available
        if (this.checkFlightsForStatus(flights, UpgradeStatus.Available)) {
          // Check if sufficient Payment and throw error if insufficient Payment
          this.checkPaymentAndThrow(flights, passengers, response.buyer, profile);
          return response as Required<GetOffersV2Response>;
        }

        // Check upgrade status reason and throw error
        [
          UpgradeStatusReason.TooCloseToDeparture,
          UpgradeStatusReason.SsrInBooking,
          UpgradeStatusReason.InfantInBooking,
          UpgradeStatusReason.GroupBooking,
          UpgradeStatusReason.TooManyPassengers,
          UpgradeStatusReason.TooFarToDeparture,
          UpgradeStatusReason.TooCloseToDepartureForWaitlist,
          UpgradeStatusReason.SegmentInRequestedUpgradeCabin,
          UpgradeStatusReason.SegmentMissingRequestedUpgradeCabin,
          UpgradeStatusReason.TicketNotIssuedByFinnair,
          UpgradeStatusReason.IneligibleBookingClass,
          UpgradeStatusReason.BookingInIneligibleStatus,
          UpgradeStatusReason.FlownSegment,
        ].forEach((reason) => {
          this.checkFlightStatusReasonAndThrow(flights, reason);
        });

        // Check if flights already on waitlist
        this.checkFlightStatusAndThrow(flights, UpgradeStatus.Waitlisted);

        // Check Some Flights already upgraded
        this.checkFlightStatusAndThrow(flights, UpgradeStatus.Success);

        // Check not Finnair flight
        this.checkNotFinnair(flights);

        const finnairFlights = flights.filter((flight) => {
          const code = flight.segmentInfo.codeShareCarrierCode || flight.segmentInfo.carrierCode;
          return FINNAIR_LIKE_AIRLINECODES.includes(code.toUpperCase());
        });

        // Check if Waitlist Period
        if (this.isWaitlistPeriod(finnairFlights)) {
          if (!profile) {
            throw new Error(UpgradeError.notLoggedIn);
          }

          if (profile.tier === ProfileTier.ACCOUNT) {
            throw new Error(UpgradeError.notAvailableAccount);
          }

          throw new Error(UpgradeError.notAvailable);
        }

        // throw not Available to handle all other cases
        throw new Error(UpgradeError.notAvailable);
      }),
      // eslint-disable-next-line rxjs/no-implicit-any-catch
      catchError((error: Error | UpgradeError) => {
        // standardise thrown errors. If thrown by new Error then
        if (error instanceof Error) {
          return throwError(() => error.message);
        }
        return throwError(() => error);
      })
    );
  }

  private isWaitlistPeriod(flights: FlightsV2): boolean {
    const lastFlight = flights.reduce((acc, cur) => {
      const date =
        !!cur.segmentInfo && !!cur.segmentInfo.departureDateTime
          ? new TzDate(cur.segmentInfo.departureDateTime).millis
          : 0;
      return !acc || date < acc ? date : acc;
    }, 0);
    return lastFlight - Date.now() >= WAITLIST_CUTOFF;
  }

  private checkFlightStatusReasonAndThrow(flights: FlightsV2, statusReason: UpgradeStatusReason) {
    const anyFlightHasStatusReason = flights.some((flight) => {
      return flight.upgrades.some((upgrade) => {
        return upgrade.upgradeStatusReason === statusReason;
      });
    });

    if (!anyFlightHasStatusReason) {
      return;
    }

    if (anyFlightHasStatusReason) {
      const errorMap = {
        [UpgradeStatusReason.TooCloseToDeparture]: UpgradeError.upgradeClosed,
        [UpgradeStatusReason.InfantInBooking]: UpgradeError.notPossible,
        [UpgradeStatusReason.GroupBooking]: UpgradeError.notPossible,
        [UpgradeStatusReason.SsrInBooking]: UpgradeError.notPossible,
        [UpgradeStatusReason.TooManyPassengers]: UpgradeError.notPossible,
        [UpgradeStatusReason.TooFarToDeparture]: UpgradeError.notPossible,
        [UpgradeStatusReason.TooCloseToDepartureForWaitlist]: UpgradeError.notPossible,
        [UpgradeStatusReason.SegmentInRequestedUpgradeCabin]: UpgradeError.notPossible,
        [UpgradeStatusReason.SegmentMissingRequestedUpgradeCabin]: UpgradeError.notPossible,
        [UpgradeStatusReason.TicketNotIssuedByFinnair]: UpgradeError.notPossible,
        [UpgradeStatusReason.IneligibleBookingClass]: UpgradeError.notPossible,
        [UpgradeStatusReason.BookingInIneligibleStatus]: UpgradeError.notPossible,
        [UpgradeStatusReason.FlownSegment]: UpgradeError.notPossible,
      };

      throw new Error(errorMap[statusReason] || UpgradeError.notPossible);
    }
  }

  private checkFlightStatusAndThrow(flights: FlightsV2, status: UpgradeStatus) {
    const hasFlightsWithStatus = this.checkFlightsForStatus(flights, status);

    if (!hasFlightsWithStatus) {
      return;
    }

    switch (status) {
      case UpgradeStatus.InProgress:
        throw new Error(UpgradeError.inProgress);
      case UpgradeStatus.Waitlisted:
        throw new Error(UpgradeError.noRemainingUpgrades);
      case UpgradeStatus.Success:
        throw new Error(UpgradeError.noRemainingUpgrades);
      default:
        throw new Error(UpgradeError.notAvailable);
    }
  }

  private checkFlightsForStatus(flights: FlightsV2, status: UpgradeStatus): boolean {
    return flights.some((flight) => {
      return flight.upgrades.find((upgrade) => {
        return upgrade.upgradeStatus === status;
      });
    });
  }

  private checkNotFinnair(flights: FlightsV2) {
    const notFinnairFlights = flights.every((flight) => {
      return flight.upgrades.every((upgrade) => {
        return upgrade.upgradeStatusReason === UpgradeStatusReason.NotFinnairFlight;
      });
    });

    if (notFinnairFlights) {
      throw new Error(UpgradeError.notFinnair);
    }
  }

  private setSummary(flights: FlightsV2): void {
    this.store$.dispatch(AuthActions.flightSummary({ summary: getFlightsAsBookingLocationCodes(flights) }));
  }

  private getMinimumPoints(flights: FlightsV2) {
    const pointsForAllUpgrades = flights
      .flatMap((flight) => {
        return flight.upgrades.map((upgrade) => {
          return upgrade?.paymentOptions?.points?.price;
        });
      })
      .filter(Boolean);

    return Math.min(...pointsForAllUpgrades) || 0;
  }

  private checkPaymentAndThrow(
    flights: FlightsV2 = [],
    passengers: Passengers,
    buyer: Buyer = {},
    profile: Profile
  ): void {
    // There should only be money options for loggedOut users so not payment checks required
    if (!profile) {
      return;
    }

    const upgradesAvailable = flights.some((flight) =>
      flight.upgrades.some((upgrade) => {
        return upgrade.upgradeStatus === UpgradeStatus.Available;
      })
    );

    if (!upgradesAvailable) {
      return;
    }

    const minPaxAmountToUpgrade = getMinPaxAmountToUpgrade(passengers);
    const minPoints = this.getMinimumPoints(flights) * minPaxAmountToUpgrade;

    const hasEnoughPoints = buyer.points >= minPoints;
    const hasEnoughVouchers =
      getVoucherAmount(VoucherType.UpgradeBenefit, buyer) >= minPaxAmountToUpgrade ||
      getVoucherAmount(VoucherType.EuropeUpgrade, buyer) >= minPaxAmountToUpgrade ||
      getVoucherAmount(VoucherType.LongHaulUpgrade, buyer) >= minPaxAmountToUpgrade;

    if (!hasEnoughPoints && !hasEnoughVouchers) {
      const hasMoneyOption = flights.some((flight) =>
        flight.upgrades.some((upgrade) => upgrade?.paymentOptions?.money)
      );
      if (!hasMoneyOption) {
        throw new Error(UpgradeError.insufficientPayment);
      }
    }
  }
}
